import React from 'react';
import { PRESET_DRUM_PATTERNS } from '../config/drumPatterns';
import type { TimeSignature, NoteDivision, DrumInstrument, DrumGridData } from '../types';

// DrumRNN 相關類型定義
interface DrumRNNNote {
  pitch: number;
  velocity: number;
  quantizedStartStep: number;
  quantizedEndStep: number;
  startTime: number;
  endTime: number;
}

interface QuantizationInfo {
  stepsPerQuarter: number;
  quantizeNoteValueLength: number;
  beatsPerBar: number;
  noteValuePerBeat: number;
}

interface PatternInfo {
  originalPattern: string;
  patternLength: number;
  repeatCount: number;
  targetSteps: number | null;
  tempo: number;
}

interface DrumRNNSeed {
  notes: DrumRNNNote[];
  totalTime: number;
  quantizationInfo: QuantizationInfo;
  patternInfo?: PatternInfo;
}

interface ConvertOptions {
  velocity?: number;
  repeatCount?: number;
  targetSteps?: number | null;
  addVariation?: boolean;
}

interface BlendOptions {
  velocity?: number;
  blendMode?: 'sequential' | 'layered' | 'alternating';
  addVariation?: boolean;
}

type BlendMode = 'sequential' | 'layered' | 'alternating';

/**
 * DrumRNN種子轉換器
 * 將preset pattern轉換為DrumRNN可用的seed序列
 */
const DrumRNNSeedConverter = {
  
  /**
   * 將樂器ID映射到MIDI音符編號
   */
  instrumentToMidiMap: {
    'BD': 36,  // Bass Drum
    'SD': 38,  // Snare Drum  
    'CH': 42,  // Closed Hi-Hat
    'OH': 46,  // Open Hi-Hat
    'CR': 49,  // Crash Cymbal
    'RD': 51,  // Ride Cymbal
    'CP': 39,  // Hand Clap
    'CB': 56,  // Cowbell
    'SH': 54,  // Tambourine/Shaker
    'T1': 45,  // Low Tom
    'T2': 47,  // Mid Tom
    'T3': 50,  // High Tom
    'AG': 67,  // Agogo bells (mapped to High Agogo)
  } as Record<string, number>,

  /**
   * 根據時間簽名和音符類型獲取量化信息
   */
  getQuantizationInfo: (timeSignature: TimeSignature = '4/4', noteDivision: NoteDivision = '16n'): QuantizationInfo => {
    const TIME_SIGNATURES: Record<TimeSignature, { beats: number; base: number }> = {
      '4/4': { beats: 4, base: 4 },
      '3/4': { beats: 3, base: 4 },
      '6/8': { beats: 6, base: 8 },
    };

    const noteLengthMap: Record<NoteDivision, number> = {
      '4n': 4,
      '8n': 8, 
      '16n': 16
    };
    
    const { beats, base } = TIME_SIGNATURES[timeSignature] || TIME_SIGNATURES['4/4'];
    const noteValue = noteLengthMap[noteDivision] || 16;
    const stepsPerQuarter = noteValue / 4;
    
    return {
      stepsPerQuarter: Math.max(1, stepsPerQuarter),
      quantizeNoteValueLength: noteValue,
      beatsPerBar: beats,
      noteValuePerBeat: base
    };
  },

  /**
   * 將preset pattern轉換為DrumRNN seed序列
   * @param patternKey - preset pattern的key
   * @param options - 轉換選項
   * @returns DrumRNN seed序列
   */
  convertPatternToSeed: (patternKey: string, options: ConvertOptions = {}): DrumRNNSeed => {
    const {
      velocity = 80,
      repeatCount = 1,
      targetSteps = null,
      addVariation = false
    } = options;

    const pattern = (PRESET_DRUM_PATTERNS as any)[patternKey];
    if (!pattern) {
      console.warn(`Pattern "${patternKey}" not found`);
      return DrumRNNSeedConverter.createEmptySeed();
    }

    const quantizationInfo = DrumRNNSeedConverter.getQuantizationInfo(
      pattern.timeSignature, 
      pattern.noteDivision
    );

    const notes: DrumRNNNote[] = [];
    const patternLength = Math.max(...Object.values(pattern.patterns).map((hits: any) => (hits as any[]).length));
    
    // 如果指定了targetSteps，計算需要的重複次數來填滿所有步數
    const finalRepeatCount = targetSteps ? Math.ceil(targetSteps / patternLength) : repeatCount;
    const maxSteps = targetSteps || (patternLength * repeatCount);

    console.log(`Converting pattern "${patternKey}" to seed: ${patternLength} steps, repeating ${finalRepeatCount} times for max ${maxSteps} steps`);
    
    // Debug: 分析原始pattern數據
    console.log('🔍 Pattern Analysis:');
    console.log('Raw pattern data:', pattern);
    const instrumentHits: Record<string, number> = {};
    Object.entries(pattern.patterns).forEach(([instrumentId, hits]) => {
      const hitCount = (hits as any[]).filter(hit => hit && hit !== 0).length;
      instrumentHits[instrumentId] = hitCount;
    });
    console.log('Hits per instrument:', instrumentHits);

    // 為每次重複生成音符，確保能填滿所有指定的步數
    for (let repeat = 0; repeat < finalRepeatCount; repeat++) {
      Object.entries(pattern.patterns).forEach(([instrumentId, hits]) => {
        (hits as any[]).forEach((hit, stepIndex) => {
          if (hit && hit !== 0) { // 確保hit是truthy且不是0
            const midiNote = DrumRNNSeedConverter.instrumentToMidiMap[instrumentId];
            if (midiNote) {
              const quantizedStep = (repeat * patternLength) + stepIndex;
              
              // 只添加在maxSteps範圍內的音符
              if (quantizedStep < maxSteps) {
                // 計算力度（加入一些變化）
                let noteVelocity = velocity;
                if (addVariation) {
                  // 根據樂器類型調整力度
                  if (instrumentId === 'BD') noteVelocity += Math.random() * 20 - 10; // Bass drum變化
                  else if (instrumentId === 'SD') noteVelocity += Math.random() * 15 - 7; // Snare變化  
                  else if (instrumentId === 'CH') noteVelocity += Math.random() * 10 - 5; // Hi-hat變化
                  
                  noteVelocity = Math.max(20, Math.min(127, Math.round(noteVelocity)));
                }

                // 計算實際時間（基於quantized steps）
                const startTime = quantizedStep / quantizationInfo.stepsPerQuarter;
                const endTime = (quantizedStep + 1) / quantizationInfo.stepsPerQuarter;
                
                notes.push({
                  pitch: midiNote,
                  velocity: noteVelocity,
                  quantizedStartStep: quantizedStep,
                  quantizedEndStep: quantizedStep + 1,
                  startTime: startTime,
                  endTime: endTime
                });
              }
            }
          }
        });
      });
    }

    const totalSteps = maxSteps;
    const totalTime = totalSteps / quantizationInfo.stepsPerQuarter;

    // Debug: 分析生成的seed
    console.log('🌱 Generated Seed Analysis:');
    console.log(`Total notes generated: ${notes.length}`);
    console.log(`Total steps: ${totalSteps}, Total time: ${totalTime}`);
    
    if (notes.length > 0) {
      const notesRange = {
        minStep: Math.min(...notes.map(n => n.quantizedStartStep)),
        maxStep: Math.max(...notes.map(n => n.quantizedEndStep)),
        minTime: Math.min(...notes.map(n => n.startTime)),
        maxTime: Math.max(...notes.map(n => n.endTime))
      };
      console.log(`Notes range: step ${notesRange.minStep} to ${notesRange.maxStep}`);
      console.log(`Time range: ${notesRange.minTime.toFixed(2)} to ${notesRange.maxTime.toFixed(2)} (span: ${(notesRange.maxTime - notesRange.minTime).toFixed(2)})`);
      console.log(`Expected time span should be: ${totalTime.toFixed(2)} quarters`);
      
      // 檢查note的有效性
      const invalidNotes = notes.filter(note => 
        !note.pitch || 
        note.quantizedStartStep >= note.quantizedEndStep ||
        note.quantizedStartStep < 0 ||
        note.velocity < 1 || note.velocity > 127
      );
      
      if (invalidNotes.length > 0) {
        console.error('❌ Generated invalid notes:', invalidNotes);
      } else {
        console.log('✅ All generated notes are valid');
      }
      
      // 示例前幾個notes
      console.log('First 3 generated notes:', notes.slice(0, 3));
    } else {
      console.warn('⚠️ No notes generated from pattern!');
    }

    return {
      notes: notes,
      totalTime: totalTime,
      quantizationInfo: quantizationInfo,
      patternInfo: {
        originalPattern: patternKey,
        patternLength: patternLength,
        repeatCount: finalRepeatCount, // 使用實際的重複次數
        targetSteps: targetSteps,
        tempo: pattern.tempo
      }
    };
  },

  /**
   * 創建空的seed序列
   */
  createEmptySeed: (timeSignature: TimeSignature = '4/4', noteDivision: NoteDivision = '16n'): DrumRNNSeed => {
    const quantizationInfo = DrumRNNSeedConverter.getQuantizationInfo(timeSignature, noteDivision);
    
    return {
      notes: [],
      totalTime: 0,
      quantizationInfo: quantizationInfo
    };
  },

  /**
   * 從網格數據創建seed序列
   * @param grid - 網格數據
   * @param instruments - 樂器列表
   * @param timeSignature - 時間簽名
   * @param noteDivision - 音符分割
   * @returns DrumRNN seed序列
   */
  convertGridToSeed: (
    grid: DrumGridData, 
    instruments: DrumInstrument[], 
    timeSignature: TimeSignature = '4/4', 
    noteDivision: NoteDivision = '16n'
  ): DrumRNNSeed => {
    const quantizationInfo = DrumRNNSeedConverter.getQuantizationInfo(timeSignature, noteDivision);
    const notes: DrumRNNNote[] = [];

    instruments.forEach(({ id }) => {
      if (grid[id]) {
        grid[id].forEach((hit, stepIndex) => {
          if (hit && hit !== null) {
            const midiNote = DrumRNNSeedConverter.instrumentToMidiMap[id];
            if (midiNote) {
              // 根據hit類型決定力度
              let velocity = 80;
              switch (hit) {
                case 'X': velocity = 110; break; // 重音
                case 'x': velocity = 80; break;  // 正常
                case 'g': velocity = 50; break;  // 鬼音符
                case 'f': velocity = 30; break;  // 輕音
                default: velocity = 80; break;
              }

              // 計算實際時間
              const startTime = stepIndex / quantizationInfo.stepsPerQuarter;
              const endTime = (stepIndex + 1) / quantizationInfo.stepsPerQuarter;
              
              notes.push({
                pitch: midiNote,
                velocity: velocity,
                quantizedStartStep: stepIndex,
                quantizedEndStep: stepIndex + 1,
                startTime: startTime,
                endTime: endTime
              });
            }
          }
        });
      }
    });

    const maxStep = notes.length > 0 ? Math.max(...notes.map(n => n.quantizedStartStep)) + 1 : 0;
    const totalTime = maxStep / quantizationInfo.stepsPerQuarter;

    return {
      notes: notes,
      totalTime: totalTime,
      quantizationInfo: quantizationInfo
    };
  },

  /**
   * 混合多個preset pattern創建seed
   * @param patternKeys - pattern key陣列
   * @param options - 混合選項
   * @returns 混合後的seed序列
   */
  blendPatternsToSeed: (patternKeys: string[], options: BlendOptions = {}): DrumRNNSeed => {
    const {
      velocity = 80,
      blendMode = 'sequential',
      addVariation = true
    } = options;

    if (!patternKeys || patternKeys.length === 0) {
      return DrumRNNSeedConverter.createEmptySeed();
    }

    if (patternKeys.length === 1) {
      return DrumRNNSeedConverter.convertPatternToSeed(patternKeys[0], {
        velocity,
        repeatCount: 1,
        addVariation
      });
    }

    // 根據混合模式處理多個pattern
    switch (blendMode) {
      case 'sequential':
        return DrumRNNSeedConverter.blendSequentially(patternKeys, { velocity, addVariation });
      case 'layered':
        return DrumRNNSeedConverter.blendLayered(patternKeys, { velocity, addVariation });
      case 'alternating':
        return DrumRNNSeedConverter.blendAlternating(patternKeys, { velocity, addVariation });
      default:
        return DrumRNNSeedConverter.convertPatternToSeed(patternKeys[0], {
          velocity,
          repeatCount: 1,
          addVariation
        });
    }
  },

  /**
   * 順序混合patterns
   */
  blendSequentially: (patternKeys: string[], options: Omit<BlendOptions, 'blendMode'>): DrumRNNSeed => {
    const allNotes: DrumRNNNote[] = [];
    let totalSteps = 0;
    let commonQuantizationInfo: QuantizationInfo | null = null;

    patternKeys.forEach((patternKey, index) => {
      const seed = DrumRNNSeedConverter.convertPatternToSeed(patternKey, options);
      
      if (index === 0) {
        commonQuantizationInfo = seed.quantizationInfo;
      }

      // 調整音符的時間偏移
      seed.notes.forEach(note => {
        allNotes.push({
          ...note,
          quantizedStartStep: note.quantizedStartStep + totalSteps,
          quantizedEndStep: note.quantizedEndStep + totalSteps
        });
      });

      totalSteps += Math.max(...seed.notes.map(n => n.quantizedStartStep)) + 1;
    });

    const finalQuantizationInfo = commonQuantizationInfo || DrumRNNSeedConverter.getQuantizationInfo();
    return {
      notes: allNotes,
      totalTime: totalSteps / finalQuantizationInfo.stepsPerQuarter,
      quantizationInfo: finalQuantizationInfo
    };
  },

  /**
   * 分層混合patterns（同時播放）
   */
  blendLayered: (patternKeys: string[], options: Omit<BlendOptions, 'blendMode'>): DrumRNNSeed => {
    const allNotes: DrumRNNNote[] = [];
    let maxSteps = 0;
    let commonQuantizationInfo: QuantizationInfo | null = null;

    patternKeys.forEach((patternKey, index) => {
      const seed = DrumRNNSeedConverter.convertPatternToSeed(patternKey, options);
      
      if (index === 0) {
        commonQuantizationInfo = seed.quantizationInfo;
      }

      allNotes.push(...seed.notes);
      if (seed.notes.length > 0) {
        maxSteps = Math.max(maxSteps, Math.max(...seed.notes.map(n => n.quantizedStartStep)) + 1);
      }
    });

    const finalQuantizationInfo = commonQuantizationInfo || DrumRNNSeedConverter.getQuantizationInfo();
    return {
      notes: allNotes,
      totalTime: maxSteps / finalQuantizationInfo.stepsPerQuarter,
      quantizationInfo: finalQuantizationInfo
    };
  },

  /**
   * 交替混合patterns
   */
  blendAlternating: (patternKeys: string[], options: Omit<BlendOptions, 'blendMode'>): DrumRNNSeed => {
    const allNotes: DrumRNNNote[] = [];
    let totalSteps = 0;
    let commonQuantizationInfo: QuantizationInfo | null = null;

    // 確定每個pattern的長度
    const patternSeeds = patternKeys.map(key => 
      DrumRNNSeedConverter.convertPatternToSeed(key, options)
    );

    if (patternSeeds.length > 0) {
      commonQuantizationInfo = patternSeeds[0].quantizationInfo;
    }

    // 交替添加patterns
    const maxCycles = 2; // 每個pattern重複2次
    for (let cycle = 0; cycle < maxCycles; cycle++) {
      patternSeeds.forEach(seed => {
        seed.notes.forEach(note => {
          allNotes.push({
            ...note,
            quantizedStartStep: note.quantizedStartStep + totalSteps,
            quantizedEndStep: note.quantizedEndStep + totalSteps
          });
        });

        totalSteps += Math.max(...seed.notes.map(n => n.quantizedStartStep)) + 1;
      });
    }

    return {
      notes: allNotes,
      totalTime: totalSteps / (commonQuantizationInfo?.stepsPerQuarter || 4),
      quantizationInfo: commonQuantizationInfo || DrumRNNSeedConverter.getQuantizationInfo()
    };
  }
};

export default DrumRNNSeedConverter; 